import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { storage } from '../utils/storage';
import { db, functions } from '../config/firebase';
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  Timestamp,
  onSnapshot
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { walletService } from '../services/walletService';

interface Entrega {
  id: string;
  nomeCliente: string;
  telefone: string;
  endereco: string;
  setor: string;
  valor: string;
  observacoes: string;
}

interface Setor {
  nome: string;
  valor: number;
}

interface SetorSelecionado {
  nome: string;
  valor: number;
  quantidade: number;
}

interface Entregador {
  id: string;
  nome: string;
}

const LancarEntregas: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [entregas, setEntregas] = useState<Entrega[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [empresaId, setEmpresaId] = useState('');
  const [empresaNome, setEmpresaNome] = useState('');
  const [empresaFoto, setEmpresaFoto] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [entregadoresDia, setEntregadoresDia] = useState<Entregador[]>([]);
  const [buscandoEntregadores, setBuscandoEntregadores] = useState(false);
  const [setores, setSetores] = useState<Setor[]>([]);
  const [usaCarteira, setUsaCarteira] = useState<boolean>(false);
  const [saldoDisponivel, setSaldoDisponivel] = useState<number>(0);
  const [taxaMotoboyAtiva, setTaxaMotoboyAtiva] = useState(false);
  const [taxaMotoboyValor, setTaxaMotoboyValor] = useState(0);
  const [entregadorSelecionado, setEntregadorSelecionado] = useState('');
  const [setoresSelecionados, setSetoresSelecionados] = useState<SetorSelecionado[]>([]);
  const [showEntregadorModal, setShowEntregadorModal] = useState(false);
  const [pesquisaSetor, setPesquisaSetor] = useState('');
  const [setoresFiltrados, setSetoresFiltrados] = useState<Setor[]>([]);
  const [pesquisaEntregador, setPesquisaEntregador] = useState('');
  const [entregadoresFiltrados, setEntregadoresFiltrados] = useState<Entregador[]>([]);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const isProcessingRef = useRef(false);

  const formatBRL = (valor: number | string) => {
    const num = typeof valor === 'string' ? parseFloat(valor) : valor;
    return num ? `R$ ${num.toFixed(2).replace('.', ',')}` : 'R$ 0,00';
  };

  // Carregar dados da empresa
  useEffect(() => {
    const carregarDadosEmpresa = async () => {
      try {
        const userId = await storage.getItem('userId');
        if (!userId) {
          navigate('/login');
          return;
        }

        const userDoc = await getDoc(doc(db, 'usuarios', userId));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const empId = data.empresaId || userId;
          setEmpresaId(empId);
          setEmpresaNome(data.nomeEmpresa || data.nome || 'Empresa');

          // Buscar dados da empresa
          const empresaDoc = await getDoc(doc(db, 'Empresa', empId));
          if (empresaDoc.exists()) {
            const empresaData = empresaDoc.data();
            setEmpresaFoto(empresaData.foto || empresaData.fotoUrl || '');
            setUsaCarteira(empresaData.usaCarteira === true);
            
            if (empresaData.usaCarteira) {
              const saldo = await walletService.getAvailableBalance(empId);
              setSaldoDisponivel(saldo);
            }

            // Carregar configurações da central da empresa
            try {
              const empresaConfigRef = doc(db, 'Empresa', empId, 'config', 'central');
              const empresaConfigSnap = await getDoc(empresaConfigRef);
              if (empresaConfigSnap.exists()) {
                const cfg = empresaConfigSnap.data() as any;
                setTaxaMotoboyAtiva(!!cfg.habilitarTaxaMotoboy);
                setTaxaMotoboyValor(Number(cfg.taxaMotoboyValor || 0));
              }
            } catch (e) {
              console.warn('Erro ao carregar config central da empresa:', e);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados da empresa:', error);
      }
    };

    carregarDadosEmpresa();
  }, [navigate]);

  // Buscar setores da empresa
  useEffect(() => {
    const buscarSetoresEmpresa = async () => {
      try {
        const userId = await storage.getItem('userId');
        if (!userId) return;

        const userDoc = await getDoc(doc(db, 'usuarios', userId));
        if (!userDoc.exists()) return;

        const userData = userDoc.data();
        const empId = userData.empresaId || userId;

        const empresaDoc = await getDoc(doc(db, 'Empresa', empId));
        if (empresaDoc.exists()) {
          const data = empresaDoc.data();
          if (data.tabelaTaxaId) {
            const tabelaDoc = await getDoc(doc(db, 'tabelasTaxas', data.tabelaTaxaId));
            if (tabelaDoc.exists()) {
              const tabela = tabelaDoc.data();
              setSetores(tabela.setores || []);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao buscar setores:', error);
      }
    };

    buscarSetoresEmpresa();
  }, []);

  // Buscar entregadores
  useEffect(() => {
    if (empresaId) {
      buscarEntregadoresDoDia();
    }
  }, [empresaId]);

  // Atualizar saldo quando modal abrir
  useEffect(() => {
    const atualizarSaldo = async () => {
      if (showAddModal && usaCarteira && empresaId) {
        try {
          const saldo = await walletService.getAvailableBalance(empresaId);
          setSaldoDisponivel(saldo);
        } catch (e) {
          console.warn('Não foi possível atualizar saldo:', e);
        }
      }
    };
    atualizarSaldo();
  }, [showAddModal, usaCarteira, empresaId]);

  // Filtrar setores
  useEffect(() => {
    if (pesquisaSetor.trim() === '') {
      setSetoresFiltrados([]);
    } else {
      const filtrados = setores.filter(setor => 
        setor.nome.toLowerCase().includes(pesquisaSetor.toLowerCase())
      );
      setSetoresFiltrados(filtrados);
    }
  }, [pesquisaSetor, setores]);

  // Filtrar entregadores
  useEffect(() => {
    if (pesquisaEntregador.trim() === '') {
      setEntregadoresFiltrados([]);
    } else {
      const filtrados = entregadoresDia.filter(entregador => 
        entregador.nome.toLowerCase().includes(pesquisaEntregador.toLowerCase())
      );
      setEntregadoresFiltrados(filtrados);
    }
  }, [pesquisaEntregador, entregadoresDia]);

  const buscarEntregadoresDoDia = async () => {
    try {
      setBuscandoEntregadores(true);
      const entregadores: {[id: string]: string} = {};

      // Estratégia 1: Buscar atribuições
      try {
        const atribuicoesRef = collection(db, 'atribuicoesEntregadores');
        const qAtribuicoes = query(
          atribuicoesRef,
          where('empresaId', '==', empresaId),
          where('ativo', '==', true)
        );
        const snapshotAtribuicoes = await getDocs(qAtribuicoes);

        for (const docSnap of snapshotAtribuicoes.docs) {
          const data = docSnap.data();
          const motoboyId = data.motoboyId;
          const motoboyNome = data.motoboyNome;
          
          if (motoboyId && motoboyNome) {
            try {
              const motoboyRef = doc(db, 'usuarios', motoboyId);
              const motoboyDoc = await getDoc(motoboyRef);
              
              if (motoboyDoc.exists()) {
                const motoboyData = motoboyDoc.data();
                const nomeCompleto = motoboyData.nome || motoboyData.nomeEmpresa || motoboyNome;
                entregadores[motoboyId] = `${nomeCompleto} (Atribuído)`;
              } else {
                entregadores[motoboyId] = `${motoboyNome} (Atribuído)`;
              }
            } catch (error) {
              entregadores[motoboyId] = `${motoboyNome} (Atribuído)`;
            }
          }
        }
      } catch (error) {
        console.error('Erro ao buscar atribuições:', error);
      }

      // Estratégia 2: Buscar histórico de entregas
      try {
        const empresaRef = doc(db, 'Empresa', empresaId);
        const entreguesRef = collection(empresaRef, 'entregues');
        const q = query(entreguesRef, orderBy('entregueEm', 'desc'));
        const snapshot = await getDocs(q);

        const motoboyIdsUnicos = new Set<string>();
        for (const docSnap of snapshot.docs) {
          const data = docSnap.data();
          if (data.motoboyId) {
            motoboyIdsUnicos.add(data.motoboyId);
          }
        }

        for (const motoboyId of motoboyIdsUnicos) {
          if (entregadores[motoboyId]) continue;

          try {
            const motoboyRef = doc(db, 'usuarios', motoboyId);
            const motoboyDoc = await getDoc(motoboyRef);
            
            if (motoboyDoc.exists()) {
              const motoboyData = motoboyDoc.data();
              const nomeCompleto = motoboyData.nome || motoboyData.nomeEmpresa || 'Motoboy';
              entregadores[motoboyId] = `${nomeCompleto} (Histórico)`;
            }
          } catch (error) {
            // Ignorar erro
          }
        }
      } catch (error) {
        console.error('Erro ao buscar histórico:', error);
      }

      // Estratégia 3: Buscar todos os entregadores ativos (último recurso)
      if (Object.keys(entregadores).length === 0) {
        try {
          const usuariosRef = collection(db, 'usuarios');
          const qUsuarios = query(
            usuariosRef,
            where('tipo', '==', 'motoboy'),
            where('ativo', '==', true)
          );
          const snapshotUsuarios = await getDocs(qUsuarios);

          for (const docSnap of snapshotUsuarios.docs) {
            const data = docSnap.data();
            const motoboyId = docSnap.id;
            const nomeCompleto = data.nome || data.nomeEmpresa || 'Entregador';
            entregadores[motoboyId] = `${nomeCompleto} (Disponível)`;
          }
        } catch (error) {
          console.error('Erro ao buscar entregadores ativos:', error);
        }
      }

      const entregadoresArray = Object.entries(entregadores)
        .map(([id, nome]) => ({ id, nome }))
        .sort((a, b) => {
          if (a.nome.includes('Atribuído') && !b.nome.includes('Atribuído')) return -1;
          if (!a.nome.includes('Atribuído') && b.nome.includes('Atribuído')) return 1;
          return a.nome.localeCompare(b.nome);
        });

      setEntregadoresDia(entregadoresArray);
    } catch (error) {
      console.error('Erro ao buscar entregadores:', error);
      setEntregadoresDia([]);
    } finally {
      setBuscandoEntregadores(false);
    }
  };

  const adicionarSetor = (setor: Setor) => {
    if (usaCarteira) {
      const taxa = taxaMotoboyAtiva ? Number(taxaMotoboyValor || 0) : 0;
      const totalSelecionados = setoresSelecionados.reduce((acc, s) => {
        const valorComTaxa = (s.valor + taxa) * s.quantidade;
        return acc + valorComTaxa;
      }, 0);
      const totalEntregasJa = entregas.reduce((acc, e) => acc + (parseFloat(e.valor) || 0), 0);
      const valorSetorComTaxa = setor.valor + taxa;
      const totalPretendido = totalSelecionados + valorSetorComTaxa + totalEntregasJa;
      
      if (totalPretendido > saldoDisponivel) {
        alert(`Saldo insuficiente. Saldo disponível: ${formatBRL(saldoDisponivel)}. Você está tentando selecionar ${formatBRL(totalPretendido)}.`);
        return;
      }
    }

    setSetoresSelecionados(prev => {
      const jaSelecionado = prev.find(s => s.nome === setor.nome);
      if (jaSelecionado) {
        return prev.map(s => s.nome === setor.nome ? { ...s, quantidade: s.quantidade + 1 } : s);
      } else {
        return [...prev, { ...setor, quantidade: 1 }];
      }
    });
  };

  const removerSetor = (setorNome: string) => {
    setSetoresSelecionados(prev => {
      const setor = prev.find(s => s.nome === setorNome);
      if (setor && setor.quantidade > 1) {
        return prev.map(s => 
          s.nome === setorNome 
            ? { ...s, quantidade: s.quantidade - 1 }
            : s
        );
      } else {
        return prev.filter(s => s.nome !== setorNome);
      }
    });
  };

  const isSetorSelecionado = (setorNome: string) => {
    return setoresSelecionados.some(s => s.nome === setorNome);
  };

  const getQuantidadeSetor = (setorNome: string) => {
    const setor = setoresSelecionados.find(s => s.nome === setorNome);
    return setor ? setor.quantidade : 0;
  };

  const calcularValorTotal = () => {
    const taxa = usaCarteira && taxaMotoboyAtiva ? Number(taxaMotoboyValor || 0) : 0;
    return setoresSelecionados.reduce((total, setor) => {
      const valorComTaxa = (setor.valor + taxa) * setor.quantidade;
      return total + valorComTaxa;
    }, 0);
  };

  const adicionarEntrega = () => {
    if (setoresSelecionados.length === 0) {
      alert('Selecione pelo menos um setor');
      return;
    }

    let entregaIndex = 0;
    setoresSelecionados.forEach((setor) => {
      for (let i = 0; i < setor.quantidade; i++) {
        const entrega: Entrega = {
          id: `${Date.now()}-${entregaIndex}`,
          nomeCliente: 'Cliente',
          telefone: '',
          endereco: 'Endereço',
          setor: setor.nome,
          valor: setor.valor.toString(),
          observacoes: '',
        };
        setEntregas(prev => [...prev, entrega]);
        entregaIndex++;
      }
    });

    setSetoresSelecionados([]);
    setPesquisaSetor('');
    setShowAddModal(false);
  };

  const removerEntrega = (id: string) => {
    setEntregas(entregas.filter(entrega => entrega.id !== id));
  };

  const removerSetorCompleto = (setorNome: string) => {
    setEntregas(entregas.filter(entrega => entrega.setor !== setorNome));
  };

  const aumentarQuantidadeSetor = (setorNome: string) => {
    const setor = setores.find(s => s.nome === setorNome);
    if (!setor) return;

    if (usaCarteira) {
      const taxa = taxaMotoboyAtiva ? Number(taxaMotoboyValor || 0) : 0;
      const valorSetorComTaxa = setor.valor + taxa;
      const totalAtual = entregas.reduce((acc, e) => acc + (parseFloat(e.valor) || 0), 0);
      const totalPretendido = totalAtual + valorSetorComTaxa;
      
      if (totalPretendido > saldoDisponivel) {
        alert(`Saldo insuficiente. Saldo disponível: ${formatBRL(saldoDisponivel)}. Você está tentando adicionar mais ${formatBRL(valorSetorComTaxa)}.`);
        return;
      }
    }

    const novaEntrega: Entrega = {
      id: `${Date.now()}-${Math.random()}`,
      nomeCliente: 'Cliente',
      telefone: '',
      endereco: 'Endereço',
      setor: setorNome,
      valor: setor.valor.toString(),
      observacoes: '',
    };
    setEntregas(prev => [...prev, novaEntrega]);
  };

  const diminuirQuantidadeSetor = (setorNome: string) => {
    const entregasDoSetor = entregas.filter(e => e.setor === setorNome);
    if (entregasDoSetor.length <= 1) {
      removerSetorCompleto(setorNome);
    } else {
      const ultimaEntrega = entregasDoSetor[entregasDoSetor.length - 1];
      removerEntrega(ultimaEntrega.id);
    }
  };

  const gerarResumoEntregas = () => {
    const totalEntregas = entregas.length;
    const taxa = usaCarteira && taxaMotoboyAtiva ? Number(taxaMotoboyValor || 0) : 0;
    
    const valorTotal = entregas.reduce((total, entrega) => {
      const frete = parseFloat(entrega.valor) || 0;
      return total + frete + taxa;
    }, 0);
    
    const setoresAgrupados = entregas.reduce((acc, entrega) => {
      if (!acc[entrega.setor]) {
        acc[entrega.setor] = { quantidade: 0, valor: 0 };
      }
      acc[entrega.setor].quantidade += 1;
      const frete = parseFloat(entrega.valor) || 0;
      acc[entrega.setor].valor += frete + taxa;
      return acc;
    }, {} as {[key: string]: {quantidade: number, valor: number}});

    const entregadorNome = entregadoresDia.find(e => e.id === entregadorSelecionado)?.nome?.split(' (')[0] || 'Não selecionado';

    return {
      totalEntregas,
      valorTotal,
      setoresAgrupados,
      entregadorNome
    };
  };

  const lancarEntregas = async () => {
    if (isProcessingRef.current) {
      return;
    }

    if (entregas.length === 0) {
      alert('Adicione pelo menos uma entrega');
      return;
    }
    if (!entregadorSelecionado) {
      alert('Selecione o entregador');
      return;
    }

    // Verificar saldo se usa carteira
    if (usaCarteira) {
      const taxa = taxaMotoboyAtiva ? Number(taxaMotoboyValor || 0) : 0;
      const totalLancar = entregas.reduce((acc, e) => {
        const frete = parseFloat(e.valor) || 0;
        return acc + frete + taxa;
      }, 0);
      
      const saldoDisponivelAtual = await walletService.getAvailableBalance(empresaId);
      if (saldoDisponivelAtual < totalLancar) {
        alert(`Saldo insuficiente. Total a lançar: ${formatBRL(totalLancar)}. Saldo disponível: ${formatBRL(saldoDisponivelAtual)}.`);
        return;
      }
    }

    isProcessingRef.current = true;
    setIsLoading(true);
    setShowLoadingModal(true);
    setLoadingMessage('Preparando entregas...');

    try {
      // Buscar valorChuva
      let valorChuva = 0;
      try {
        const empresaConfigRef = doc(db, 'Empresa', empresaId, 'config', 'central');
        const empresaConfigSnap = await getDoc(empresaConfigRef);
        if (empresaConfigSnap.exists()) {
          const cfg = empresaConfigSnap.data() as any;
          if (cfg?.habilitarValorChuva && cfg?.valorChuva !== undefined && cfg?.valorChuva !== null && cfg?.valorChuva !== '') {
            const valorStr = String(cfg.valorChuva);
            const valorLimpo = valorStr.replace(',', '.');
            const valorNum = parseFloat(valorLimpo);
            if (!isNaN(valorNum) && valorNum > 0) {
              valorChuva = valorNum;
            }
          } else {
            const globalCfgRef = doc(db, 'configuracoes', 'central');
            const globalCfgSnap = await getDoc(globalCfgRef);
            if (globalCfgSnap.exists()) {
              const gcfg = globalCfgSnap.data() as any;
              valorChuva = Number(gcfg.valorChuva || 0);
            }
          }
        } else {
          const globalCfgRef = doc(db, 'configuracoes', 'central');
          const globalCfgSnap = await getDoc(globalCfgRef);
          if (globalCfgSnap.exists()) {
            const gcfg = globalCfgSnap.data() as any;
            valorChuva = Number(gcfg.valorChuva || 0);
          }
        }
      } catch (error) {
        console.warn('Erro ao buscar valorChuva:', error);
      }

      const callable = httpsCallable(functions, 'lancarEntregasEmLote');
      setLoadingMessage(`Enviando ${entregas.length} entrega(s) para processamento...`);

      const resultado = await callable({
        entregas: entregas,
        empresaId: empresaId,
        entregadorSelecionado: entregadorSelecionado,
        usaCarteira: usaCarteira,
        taxaMotoboyValor: taxaMotoboyAtiva ? Number(taxaMotoboyValor || 0) : 0,
        valorChuva: valorChuva,
        empresaNome: empresaNome || 'Empresa'
      });

      if ((resultado.data as any)?.sucesso) {
        setLoadingMessage('Enviando notificação para o entregador...');
        
        try {
          const pushCallable = httpsCallable(functions, 'sendPushManual');
          await pushCallable({
            title: 'Entregas Adicionadas',
            body: `${empresaNome || 'Empresa'} adicionou ${(resultado.data as any).totalProcessadas} entrega${(resultado.data as any).totalProcessadas > 1 ? 's' : ''} para você!`,
            target: 'motoboy',
            motoboyId: entregadorSelecionado,
          });
        } catch (pushError) {
          console.error('Erro ao enviar notificação:', pushError);
        }

        setShowLoadingModal(false);
        setIsLoading(false);
        isProcessingRef.current = false;

        const mensagemSucesso = (resultado.data as any).erros && (resultado.data as any).erros.length > 0
          ? `${(resultado.data as any).totalProcessadas} entrega(s) lançada(s) com sucesso!\n\n⚠️ ${(resultado.data as any).erros.length} lote(s) com erro foram ignorados.`
          : `${(resultado.data as any).totalProcessadas} entrega(s) lançada(s) com sucesso!`;

        alert(mensagemSucesso);
        setEntregas([]);
        setEntregadorSelecionado('');
        navigate('/empresa');
      } else {
        throw new Error('Cloud Function retornou erro');
      }
    } catch (error: any) {
      console.error('Erro ao lançar entregas:', error);
      setShowLoadingModal(false);
      setIsLoading(false);
      isProcessingRef.current = false;

      let mensagemErro = 'Não foi possível lançar as entregas';
      if (error.code === 'functions/invalid-argument') {
        mensagemErro = 'Dados inválidos fornecidos para o processamento';
      } else if (error.code === 'functions/unauthenticated') {
        mensagemErro = 'Usuário não autenticado';
      } else if (error.message?.includes('Saldo insuficiente')) {
        mensagemErro = 'Saldo insuficiente na carteira da empresa';
      }

      alert(mensagemErro);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const resumo = gerarResumoEntregas();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500">
      {/* Header */}
      <header className="bg-white shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowMenu(true)}
                className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Lançar Entregas</h1>
                <p className="text-sm text-gray-600">Adicione entregas para o entregador</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/empresa')}
              className="text-gray-700 hover:text-gray-900 flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <span>←</span>
              <span>Voltar</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Card de Resumo */}
        {entregas.length > 0 && (
          <div className="bg-white rounded-2xl shadow-2xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">Resumo das Entregas</h2>
              <div className="text-right">
                <p className="text-sm text-gray-600">Total de Entregas</p>
                <p className="text-2xl font-bold text-blue-600">{resumo.totalEntregas}</p>
              </div>
            </div>

            <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
              {Object.entries(resumo.setoresAgrupados).map(([setor, dados]) => {
                const taxa = usaCarteira && taxaMotoboyAtiva ? Number(taxaMotoboyValor || 0) : 0;
                const valorUnitario = dados.valor / dados.quantidade;
                const valorFrete = valorUnitario - taxa;

                return (
                  <div key={setor} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="font-semibold text-gray-900">{setor}</span>
                        </div>
                        <p className="text-sm text-gray-600">
                          {formatBRL(valorFrete)}{taxa > 0 ? ` + ${formatBRL(taxa)}` : ''} = {formatBRL(valorUnitario)}/un
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-1 border border-gray-200">
                          <button
                            onClick={() => diminuirQuantidadeSetor(setor)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                          </button>
                          <span className="font-bold text-gray-900 min-w-[2rem] text-center">{dados.quantidade}</span>
                          <button
                            onClick={() => aumentarQuantidadeSetor(setor)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{formatBRL(dados.valor)}</p>
                        </div>
                        <button
                          onClick={() => {
                            if (confirm(`Remover todas as ${dados.quantidade} entrega(s) do setor "${setor}"?`)) {
                              removerSetorCompleto(setor);
                            }
                          }}
                          className="text-red-600 hover:text-red-800 p-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-gray-200 pt-4 flex items-center justify-between">
              <span className="text-lg font-semibold text-gray-900">Total Geral:</span>
              <span className="text-2xl font-bold text-blue-600">{formatBRL(resumo.valorTotal)}</span>
            </div>

            {usaCarteira && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Saldo Disponível:</span>
                  <span className="font-semibold text-gray-900">{formatBRL(saldoDisponivel)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Estado vazio */}
        {entregas.length === 0 && (
          <div className="bg-white rounded-2xl shadow-2xl p-12 text-center">
            <div className="text-6xl mb-4">📦</div>
            <p className="text-gray-600 text-lg font-medium mb-2">Nenhuma entrega adicionada</p>
            <p className="text-gray-500">Clique no botão + para lançar entregas</p>
          </div>
        )}

        {/* Botão Lançar */}
        {entregas.length > 0 && (
          <div className="fixed bottom-6 right-6 z-30">
            <button
              onClick={lancarEntregas}
              disabled={isLoading || !entregadorSelecionado}
              className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-3"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Lançando...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  <span>Lançar {entregas.length} Entrega(s)</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Botão Flutuante + */}
        <button
          onClick={() => setShowAddModal(true)}
          className="fixed bottom-6 left-6 w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 flex items-center justify-center z-30"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </main>

      {/* Modal Adicionar Entrega */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white flex items-center justify-between">
              <h2 className="text-xl font-bold">Adicionar Entregas</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSetoresSelecionados([]);
                  setPesquisaSetor('');
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Seletor de Entregador */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Entregador *</label>
                <button
                  onClick={() => setShowEntregadorModal(true)}
                  className={`w-full px-4 py-3 border-2 rounded-xl text-left transition-all ${
                    entregadorSelecionado
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={entregadorSelecionado ? 'text-gray-900 font-medium' : 'text-gray-500'}>
                      {entregadorSelecionado
                        ? entregadoresDia.find(e => e.id === entregadorSelecionado)?.nome?.split(' (')[0] || 'Selecionar entregador'
                        : 'Selecione o entregador'}
                    </span>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
              </div>

              {/* Setores Selecionados */}
              {setoresSelecionados.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Setores Selecionados</label>
                  <div className="flex flex-wrap gap-2">
                    {setoresSelecionados.map((setor, idx) => (
                      <div key={`${setor.nome}-${idx}`} className="bg-blue-100 text-blue-800 px-3 py-2 rounded-lg flex items-center gap-2">
                        <span className="font-medium">{setor.nome}</span>
                        <div className="flex items-center gap-1 bg-white rounded px-2">
                          <button
                            onClick={() => removerSetor(setor.nome)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                          </button>
                          <span className="font-bold mx-1">{setor.quantidade}</span>
                          <button
                            onClick={() => adicionarSetor({ nome: setor.nome, valor: setor.valor })}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Busca de Setores */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Buscar Setores</label>
                <input
                  type="text"
                  value={pesquisaSetor}
                  onChange={(e) => setPesquisaSetor(e.target.value)}
                  placeholder="Digite para buscar setores..."
                  className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 text-gray-900"
                />
              </div>

              {/* Lista de Setores */}
              {setoresFiltrados.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {setoresFiltrados.map((setor, idx) => {
                    const selecionado = isSetorSelecionado(setor.nome);
                    const quantidade = getQuantidadeSetor(setor.nome);
                    const taxa = usaCarteira && taxaMotoboyAtiva ? Number(taxaMotoboyValor || 0) : 0;
                    const valorFinal = setor.valor + taxa;

                    return (
                      <div
                        key={`${setor.nome}-${idx}`}
                        className={`p-4 border-2 rounded-xl transition-all ${
                          selecionado
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              selecionado
                                ? 'border-blue-600 bg-blue-600'
                                : 'border-gray-300'
                            }`}>
                              {selecionado && (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <span className={`font-medium ${selecionado ? 'text-blue-900' : 'text-gray-900'}`}>
                              {setor.nome}
                            </span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className={`font-bold ${selecionado ? 'text-blue-600' : 'text-gray-700'}`}>
                              {formatBRL(valorFinal)}
                            </span>
                            {selecionado && (
                              <div className="flex items-center gap-2 bg-white rounded-lg px-2 py-1 border border-gray-200">
                                <button
                                  onClick={() => removerSetor(setor.nome)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                  </svg>
                                </button>
                                <span className="font-bold mx-1 min-w-[1.5rem] text-center">{quantidade}</span>
                                <button
                                  onClick={() => adicionarSetor(setor)}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                </button>
                              </div>
                            )}
                            {!selecionado && (
                              <button
                                onClick={() => adicionarSetor(setor)}
                                className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                              >
                                Adicionar
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {pesquisaSetor.trim() === '' 
                    ? 'Digite para buscar setores...' 
                    : 'Nenhum setor encontrado'}
                </div>
              )}

              {/* Total Selecionado */}
              {setoresSelecionados.length > 0 && (
                <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-900">Total selecionado:</span>
                    <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">{formatBRL(calcularValorTotal())}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex items-center gap-4">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSetoresSelecionados([]);
                  setPesquisaSetor('');
                }}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={adicionarEntrega}
                disabled={setoresSelecionados.length === 0}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                Lançar Entregas
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Selecionar Entregador */}
      {showEntregadorModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col border border-white/20">
            <div className="p-6 bg-gradient-to-r from-blue-600 to-cyan-600 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 backdrop-blur-sm p-2 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold">Selecionar Entregador</h2>
              </div>
              <button
                onClick={() => setShowEntregadorModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={pesquisaEntregador}
                  onChange={(e) => setPesquisaEntregador(e.target.value)}
                  placeholder="Buscar entregador..."
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {(pesquisaEntregador.trim() === '' ? entregadoresDia : entregadoresFiltrados).length > 0 ? (
                <div className="space-y-2">
                  {(pesquisaEntregador.trim() === '' ? entregadoresDia : entregadoresFiltrados).map((entregador) => (
                    <button
                      key={entregador.id}
                      onClick={() => {
                        setEntregadorSelecionado(entregador.id);
                        setShowEntregadorModal(false);
                        setPesquisaEntregador('');
                      }}
                      className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                        entregadorSelecionado === entregador.id
                          ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-cyan-50 shadow-md'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${entregadorSelecionado === entregador.id ? 'text-blue-900' : 'text-gray-900'}`}>
                          {entregador.nome.split(' (')[0]}
                        </span>
                        {entregadorSelecionado === entregador.id && (
                          <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full p-1">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </div>
                      {entregador.nome.includes('(') && (
                        <p className="text-sm text-gray-500 mt-1">
                          {entregador.nome.split('(')[1].replace(')', '')}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">👤</div>
                  <p className="text-gray-600 font-medium">Nenhum entregador encontrado</p>
                  <button
                    onClick={buscarEntregadoresDoDia}
                    disabled={buscandoEntregadores}
                    className="mt-4 px-6 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
                  >
                    {buscandoEntregadores ? 'Buscando...' : 'Buscar Entregadores'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Loading */}
      {showLoadingModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-white/20">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full mb-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Lançando Entregas</h3>
              <p className="text-gray-600">{loadingMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Menu Lateral */}
      {showMenu && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
            onClick={() => setShowMenu(false)}
          />
          <div className="fixed inset-y-0 left-0 w-80 bg-white/95 backdrop-blur-xl shadow-2xl z-50 overflow-y-auto border-r border-white/20 transform transition-transform">
            <div className="p-6 bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600 text-white">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Menu</h2>
                <button
                  onClick={() => setShowMenu(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex flex-col items-center">
                {empresaFoto ? (
                  <img
                    src={empresaFoto}
                    alt={empresaNome}
                    className="w-20 h-20 rounded-2xl border-4 border-white/30 shadow-xl mb-3 object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm border-4 border-white/30 shadow-xl mb-3 flex items-center justify-center">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                )}
                <p className="text-lg font-bold">{empresaNome || 'Empresa'}</p>
                <p className="text-sm text-blue-100 mt-1">Painel Administrativo</p>
              </div>
            </div>

            <nav className="p-4 space-y-2">
              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/empresa');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Dashboard</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/status-pedido');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Pedidos</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/perfil');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Perfil</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/relatorio');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Histórico de Entregas</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/tabela-taxas');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Tabela de Taxas</span>
              </button>

              <div className="pt-4 mt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowMenu(false);
                    handleLogout();
                  }}
                  className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all flex items-center gap-4 group"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </div>
                  <span className="font-semibold text-red-600">Sair</span>
                </button>
              </div>
            </nav>
          </div>
        </>
      )}
    </div>
  );
};

export default LancarEntregas;

